import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const SearchViewModal = ({
  open = false,
  onClose = () => {},
  onBack = () => {},
  searchResults = [],
  placeholder = "Input text",
  variant = "standard", // standard, fullscreen, compact
  sx = {},
  ...props
}) => {
  // Configure variant-specific settings
  const isFullscreen = variant === "fullscreen";
  const isCompact = variant === "compact";
  const showBackButton = !isCompact;
  const showCloseButton = true;
  const [searchValue, setSearchValue] = useState("");

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      // Handle search action
      console.log('Searching for:', searchValue);
    }
  };

  const handleBack = () => {
    setSearchValue("");
    onBack();
  };

  const handleClose = () => {
    setSearchValue("");
    onClose();
  };

  const defaultSearchResults = [
    {
      id: 1,
      title: "List item",
      subtitle: "Supporting line text lorem ipsum dolor...",
      avatar: "A"
    },
    {
      id: 2,
      title: "List item", 
      subtitle: "Supporting line text lorem ipsum dolor...",
      avatar: "A"
    },
    {
      id: 3,
      title: "List item",
      subtitle: "Supporting line text lorem ipsum dolor...", 
      avatar: "A"
    }
  ];

  const resultsToShow = searchResults.length > 0 ? searchResults : defaultSearchResults;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isFullscreen}
      maxWidth={isCompact ? "xs" : isFullscreen ? "lg" : "sm"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isFullscreen ? 0 : 3,
          minHeight: isFullscreen ? '100vh' : isCompact ? '300px' : '500px',
          maxHeight: isFullscreen ? '100vh' : isCompact ? '400px' : '600px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
          ...sx
        }
      }}
      {...props}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Search Header */}
        <Box sx={{ 
          borderBottom: '1px solid #E7DFEC',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {showBackButton && (
            <IconButton
              onClick={handleBack}
              sx={{ 
                color: '#49454F',
                padding: '8px'
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          
          <TextField
            value={searchValue}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            variant="standard"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {showCloseButton && (
                    <IconButton
                      onClick={handleClose}
                      sx={{ 
                        color: '#49454F',
                        padding: '4px'
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
              disableUnderline: true,
              sx: {
                fontSize: '16px',
                '& input': {
                  padding: '8px 12px',
                  fontSize: '16px',
                  color: '#1D192B',
                  '&::placeholder': {
                    color: '#79747E',
                    opacity: 1
                  }
                }
              }
            }}
          />
        </Box>

        {/* Search Results */}
        <Box sx={{ 
          maxHeight: isFullscreen ? 'calc(100vh - 80px)' : isCompact ? '300px' : '500px',
          overflow: 'auto'
        }}>
          <List sx={{ p: 0 }}>
            {/* Render different content based on variant */}
            {isCompact ? (
              // Compact variant - only 3 simple items
              resultsToShow.slice(0, 3).map((item, index) => (
                <ListItem 
                  key={item.id || index}
                  sx={{ 
                    px: 1.5,
                    py: 1,
                    '&:hover': {
                      backgroundColor: '#F3F0F8',
                      cursor: 'pointer'
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        backgroundColor: '#8E4EC6',
                        color: 'white',
                        width: 32,
                        height: 32,
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      {item.avatar || 'A'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ 
                        fontWeight: 400, 
                        fontSize: '14px',
                        color: '#1D192B'
                      }}>
                        {item.title || 'List item'}
                      </Typography>
                    }
                  />
                </ListItem>
              ))
            ) : (
              // Standard and Fullscreen variants - full layout with hinted sections
              <>
                {/* First group of results */}
                {resultsToShow.slice(0, 3).map((item, index) => (
                  <ListItem 
                    key={item.id || index}
                    sx={{ 
                      px: 2,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: '#F3F0F8',
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          backgroundColor: '#8E4EC6',
                          color: 'white',
                          width: 40,
                          height: 40,
                          fontSize: '16px',
                          fontWeight: 500
                        }}
                      >
                        {item.avatar || 'A'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ 
                          fontWeight: 400, 
                          fontSize: '16px',
                          color: '#1D192B',
                          mb: 0.5 
                        }}>
                          {item.title || 'List item'}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ 
                          color: '#79747E',
                          fontSize: '14px',
                          fontWeight: 400
                        }}>
                          {item.subtitle || 'Supporting line text lorem ipsum dolor...'}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}

                {/* Hinted search text section */}
                <Box sx={{ 
                  px: 2, 
                  py: 1.5,
                  backgroundColor: '#F3F0F8',
                  borderLeft: '4px solid #8E4EC6'
                }}>
                  <Typography variant="body2" sx={{ 
                    color: '#79747E',
                    fontSize: '14px',
                    fontWeight: 400
                  }}>
                    Hinted search text
                  </Typography>
                </Box>

                {/* Second group of results */}
                {resultsToShow.slice(0, 3).map((item, index) => (
                  <ListItem 
                    key={`second-${item.id || index}`}
                    sx={{ 
                      px: 2,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: '#F3F0F8',
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          backgroundColor: '#8E4EC6',
                          color: 'white',
                          width: 40,
                          height: 40,
                          fontSize: '16px',
                          fontWeight: 500
                        }}
                      >
                        {item.avatar || 'A'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ 
                          fontWeight: 400, 
                          fontSize: '16px',
                          color: '#1D192B',
                          mb: 0.5 
                        }}>
                          {item.title || 'List item'}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ 
                          color: '#79747E',
                          fontSize: '14px',
                          fontWeight: 400
                        }}>
                          {item.subtitle || 'Supporting line text lorem ipsum dolor...'}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}

                {isFullscreen && (
                  <>
                    {/* Another Hinted search text section for fullscreen */}
                    <Box sx={{ 
                      px: 2, 
                      py: 1.5,
                      backgroundColor: '#F3F0F8',
                      borderLeft: '4px solid #8E4EC6'
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: '#79747E',
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Hinted search text
                      </Typography>
                    </Box>

                    {/* Third group of results for fullscreen */}
                    {resultsToShow.slice(0, 3).map((item, index) => (
                      <ListItem 
                        key={`third-${item.id || index}`}
                        sx={{ 
                          px: 2,
                          py: 1.5,
                          '&:hover': {
                            backgroundColor: '#F3F0F8',
                            cursor: 'pointer'
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              backgroundColor: '#8E4EC6',
                              color: 'white',
                              width: 40,
                              height: 40,
                              fontSize: '16px',
                              fontWeight: 500
                            }}
                          >
                            {item.avatar || 'A'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body1" sx={{ 
                              fontWeight: 400, 
                              fontSize: '16px',
                              color: '#1D192B',
                              mb: 0.5 
                            }}>
                              {item.title || 'List item'}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" sx={{ 
                              color: '#79747E',
                              fontSize: '14px',
                              fontWeight: 400
                            }}>
                              {item.subtitle || 'Supporting line text lorem ipsum dolor...'}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </>
                )}
              </>
            )}
          </List>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SearchViewModal;
